import { store } from '@/context/store';
import {
    EParameters,
    getCaseInsensitiveParam,
    getParameters,
} from '@/utils/setParametersFromParams';

/**
 * Who and where is this session for?
 *
 * Apply is a white-label form embedded across many partner sites, as a code snippet and as
 * an iframe, on several of our own domains. Downstream every quote reaches Bifrost as
 * partnerId=vroom, so without this the whole estate collapses into one indistinguishable
 * stream. The API turns these fields into `onBehalfOf` before calling Bifrost, and the
 * tracking session derives its embed page from them (see embedPage.ts).
 *
 * HOW THE WIDGET CAN BE EMBEDDED (`embedMode`):
 *   snippet  the loader script rendered us into the partner's own DOM, so window.location
 *            IS the partner page.
 *   iframe   the document is OUR domain. The parent page is visible only to JS inside the
 *            iframe, and the browser deliberately shows it very little:
 *              `parentUrl`       document.referrer — origin only under every browser's
 *                                default referrer policy; the path arrives only if the
 *                                partner relaxes that policy (Chrome/Edge honour it,
 *                                Safari and Firefox do not).
 *              `ancestorOrigin`  location.ancestorOrigins — the top-level page's origin,
 *                                never its path. Supported everywhere since Firefox 148.
 *            Reading window.parent.location throws cross-origin. No header, API or CSP
 *            report exposes the path either — verified against the specs and live.
 *   direct   the widget is the top-level document on one of our own hosts: an SMS or QR
 *            link, a Dealer Studio hand-off, or a partner linking out. There is no host
 *            page; document.referrer here is merely where the visitor came from, which is
 *            why it is NOT reported as `parentUrl` in this mode.
 *
 * `pageUrl` is the partner's `url=` parameter on the iframe src. It is the only source of
 * the exact page that works in every browser, and the largest partners already send it.
 *
 * Wrapped in try/catch: cross-origin property access can throw and redux may not be ready.
 * This must never be able to stop a quote or a tracking batch.
 */
export type EmbedMode = 'iframe' | 'snippet' | 'direct';

export interface IEmbedContext {
    embedMode: EmbedMode | null;
    pageUrl: string | null;
    parentUrl: string | null;
    ancestorOrigin: string | null;
    vroomCode: string | null;
    storeId: string | null;
    brandId: string | null;
    organisationId: string | null;
}

// Everything the browser tells us, gathered in one place so the resolution rules
// below can be tested without a DOM.
export interface IEmbedEnvironment {
    isSnippet: boolean;
    isEmbedded: boolean;
    referrer: string;
    ancestorOrigins: string[];
    /** window.location.origin — the site itself in snippet and direct mode */
    locationOrigin: string;
    pageUrlParam: string | null;
}

type ResolvedEmbed = Pick<IEmbedContext, 'embedMode' | 'pageUrl' | 'parentUrl' | 'ancestorOrigin'>;

const EMPTY: IEmbedContext = {
    embedMode: null,
    pageUrl: null,
    parentUrl: null,
    ancestorOrigin: null,
    vroomCode: null,
    storeId: null,
    brandId: null,
    organisationId: null,
};

// A partner's `url=` value is untrusted input on a public URL, and it is typed by hand
// into a CMS template, so it is read leniently but never trusted: a missing scheme is
// assumed to be https, a protocol-relative "//host/path" is completed, and a bare path
// is resolved against the site the browser already told us about. Anything that still
// is not http(s) — a javascript: scheme, a data: URL, a lone word — is discarded.
//
// The query string and fragment are dropped here, before the value is sent anywhere:
// nothing downstream wants them, and a partner's query string may carry an email or a
// token that has no business in our request logs.
const MAX_PAGE_URL_LENGTH = 2000;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function parseLeniently(value: string, siteOrigin: string | null): URL | null {
    if (HAS_SCHEME.test(value)) return new URL(value);
    if (value.startsWith('//')) return new URL(`https:${value}`);
    if (value.startsWith('/')) return siteOrigin ? new URL(value, siteOrigin) : null;
    return new URL(`https://${value}`);
}

// A CMS helper that encodes an already-encoded address hands us "https%3A%2F%2F…" after
// the query string is decoded once. One more decode recovers the page.
function decodeIfDoubleEncoded(value: string): string {
    if (value.includes('://') || !/%3A%2F%2F/i.test(value)) return value;
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

export function safeHttpUrl(raw: string | null | undefined, siteOrigin: string | null = null): string | null {
    const value = decodeIfDoubleEncoded((raw ?? '').trim());
    if (!value || value.length > MAX_PAGE_URL_LENGTH) return null;

    try {
        const parsed = parseLeniently(value, siteOrigin);
        if (!parsed) return null;
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
        // "https://hello" parses; a site does not look like that.
        if (!parsed.hostname.includes('.') && parsed.hostname !== 'localhost') return null;
        return `${parsed.origin}${parsed.pathname}`;
    } catch {
        return null;
    }
}

function originOf(url: string): string | null {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
}

export function resolveEmbedContext(env: IEmbedEnvironment): ResolvedEmbed {
    // The snippet check comes first: a partner page that is itself inside an iframe is
    // still the page the widget lives on.
    const embedMode: EmbedMode = env.isSnippet ? 'snippet' : env.isEmbedded ? 'iframe' : 'direct';
    const inIframe = embedMode === 'iframe';

    // ancestorOrigins runs from the immediate parent up to the top-level page. Site
    // builders such as Wix wrap embeds in a sandbox frame, so the immediate parent can be
    // filesusr.com when the site is the partner's — the top-level origin is the site. An
    // entry serialises as "null" when the parent redacted it with referrerpolicy.
    const topOrigin =
        [...env.ancestorOrigins].reverse().find((origin) => origin && origin !== 'null') ?? null;

    // The site a bare-path `url=` belongs to: the page around the iframe, or the
    // document itself when the widget is the page.
    const siteOrigin = inIframe ? topOrigin ?? originOf(env.referrer) : env.locationOrigin || null;

    return {
        embedMode,
        pageUrl: safeHttpUrl(env.pageUrlParam, siteOrigin),
        parentUrl: inIframe ? env.referrer || null : null,
        ancestorOrigin: inIframe ? topOrigin : null,
    };
}

// The loader script defines this global on the host page before loading the bundle.
// Referencing an undeclared identifier throws, hence the try/catch. Truthiness mirrors
// App.tsx, which is what actually decides to render in snippet mode.
function isSnippetEmbed(): boolean {
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-undef
        return !!VroomCodeSnippetQueryStringParams;
    } catch {
        return false;
    }
}

function readEnvironment(): IEmbedEnvironment {
    let ancestorOrigins: string[] = [];
    try {
        ancestorOrigins = Array.from(window.location.ancestorOrigins ?? []);
    } catch {
        ancestorOrigins = [];
    }

    // getParameters() already reads the snippet global or window.location.search, and
    // the key is matched case-insensitively like every other prefill parameter.
    let pageUrlParam: string | null = null;
    try {
        pageUrlParam = getCaseInsensitiveParam(getParameters(), EParameters.ORIGINAL_URL);
    } catch {
        pageUrlParam = null;
    }

    return {
        isSnippet: isSnippetEmbed(),
        isEmbedded: window.self !== window.top,
        referrer: document.referrer || '',
        ancestorOrigins,
        locationOrigin: window.location.origin || '',
        pageUrlParam,
    };
}

export function getEmbedContext(): IEmbedContext {
    try {
        if (typeof window === 'undefined') return EMPTY;

        let partner: any = {};
        try {
            partner = (store.getState() as any)?.displayReducer?.partner ?? {};
        } catch {
            partner = {};
        }

        return {
            ...resolveEmbedContext(readEnvironment()),
            // partnerId here is the vroom code that loaded branding/config on page load.
            vroomCode: partner.partnerId ?? null,
            storeId: partner.partnershipConfigId ?? null,
            brandId: partner.brandId ?? null,
            organisationId: partner.organisationId ?? null,
        };
    } catch {
        return EMPTY;
    }
}
